import Model, {ModelSetting} from "../../../model/Model";
import Plan from "../../../model/Plan";
import RateModels, {RateModel} from "../../../components/calculator/RateModels";
import {useEffect, useState} from "react";
import {Grid, Typography} from "@mui/material";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {getGrowthModelLabel, GrowthModel} from "../../../model/GrowthStrategy";
import * as React from "react";
import Visible from "../../../components/Visible";
import SimpleGrowth from "../../../images/SimpleGrowth.png";
import AgressivePie from "../../../images/7030pie.png";
import BalancedPie from "../../../images/6040pie.png";
import ModeratePie from "../../../images/5050pie.png";
import {useResources} from "../../../stores/ResourceProvider";

const MonteCarloDialog = ({
  plan,
  model,
  onClose,
  onSave
} : {
  plan: Plan
  model: Model
  onClose(): void
  onSave(model: Model): void
}) => {
  const [rateModel, setRateModel] = useState<RateModel>(RateModel.Moderate)
  const [simulations, setSimulations] = useState<string>("1000")

  const {modelStore} = useResources()
  const rateModels = new RateModels()

  const styles = {
    detailText: {
      fontSize: 14
    }
  }

  useEffect(() => {
    setRateModel(model.getSetting(ModelSetting.MonteCarloRateModel, RateModel.Moderate))
    setSimulations(model.getSetting(ModelSetting.MonteCarloSimulations, "1000"))
  }, [model])

  const handleClose = () => {
    onClose()
  }

  const handleSave = async () => {
    model.setSetting(ModelSetting.MonteCarloRateModel, rateModel)
    model.setSetting(ModelSetting.MonteCarloSimulations, simulations)
    const update = await modelStore.saveSettings(model)
    if (update) {
      onSave(update)
    }
  }

  const rateModelOptions = [RateModel.Moderate, RateModel.Balanced, RateModel.Aggressive]

  return (
    <ModelEditDialog title="Monte Carlo Settings" open={true} size="md"
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      <Grid container>
        <Grid item xs={12} sm={6}>
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            name="rateModel"
            label="Portfolio Model"
            variant="standard"
            autocompleteOptions={{
              freeSolo: false,
              options: rateModelOptions,
              getOptionLabel: (option: RateModel) => {
                return(rateModels.getRateModelLabel(option))
              },
              value: rateModel,
              onChange: (event: any, value: RateModel, reason: any) => {
                setRateModel(value)
              }
            }}
          />
          <TextFieldValidator
            type="number"
            validators={{ required: true, minValue: 100, maxValue: 1000 }}
            name="simulations"
            variant="standard"
            margin="dense"
            label="Number of simulations"
            value={simulations}
            onChange={(event: any) => setSimulations(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Visible cond={rateModel === RateModel.Aggressive}>
            <Grid container>
              <Grid item xs={4} textAlign="center">
                <img src={AgressivePie} style={{width:"80%", marginTop:"4px"}}/>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="h5">
                  Aggressive - 70/30 Stocks/Bonds
                </Typography>
                <Typography variant="body2" style={styles.detailText}>
                  This model represents an aggressive blend of stocks (70% of the portfolio as represented by a U.S. large cap stock index) and bonds (30% of the portfolio as represented by intermediate term U.S. Treasury bonds) and assumes annual re-balancing, dividends re-invested and no fees.
                </Typography>
              </Grid>
            </Grid>
          </Visible>
          <Visible cond={rateModel === RateModel.Balanced}>
            <Grid container>
              <Grid item xs={4} textAlign="center">
                <img src={BalancedPie} style={{width:"80%", marginTop:"4px"}}/>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="h5">
                  Balanced - 60/40 Stocks/Bonds
                </Typography>
                <Typography variant="body2" style={styles.detailText}>
                  This model represents a balanced blend of stocks (60% of the portfolio as represented by a U.S. large cap stock index) and bonds (40% of the portfolio as represented by intermediate term U.S. Treasury bonds) and assumes annual re-balancing, dividends re-invested and no fees.
                </Typography>
              </Grid>
            </Grid>
          </Visible>
          <Visible cond={rateModel === RateModel.Moderate}>
            <Grid container>
              <Grid item xs={4} textAlign="center">
                <img src={ModeratePie} style={{width:"80%", marginTop:"4px"}}/>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="h5">
                  Moderate - 50/50 Stocks/Bonds
                </Typography>
                <Typography variant="body2" style={styles.detailText}>
                  This model represents a moderate blend of stocks (50% of the portfolio as represented by a U.S. large cap stock index) and bonds (50% of the portfolio as represented by intermediate term U.S. Treasury bonds) and assumes annual re-balancing, dividends re-invested and no fees.
                </Typography>
              </Grid>
            </Grid>
          </Visible>
        </Grid>
      </Grid>
    </ModelEditDialog>
  )
}

export default MonteCarloDialog